import { createSlice } from "@reduxjs/toolkit"

export interface DashboardItem {
  id: number;
  mainIcon: string;
  mainIconClass: string;
  label: string;
  badgeClass: string;
  icon: string;
  percentage: string;
  caption: string;
  subCounter: { id: number; counter: number; suffix: string }[];
}

export interface SummaryItem {
  totaluser: number;
  totalticket: number;
  total_lead: number;
  total_insurance: number;
  total_active_user: number;
  total_inactiveuser: number;
}

export interface DoorstepDeliverySummary {
  delivstatus: string;
  totalcount: number;
  totalamount: number;
}

export interface AICourseSummary {
  description: string;
  totalcount: number;
}

export interface DaybookSummaryItem {
  label: string;
  amount: number;
  count: number;
  description?: string;
}

export interface DashboardSummaryData {
  summary: SummaryItem[];
  doorstepdelivery_summary: DoorstepDeliverySummary[];
  daybook_summary: DaybookSummaryItem[];
  AIcourse_summary: AICourseSummary[];
}

export interface DashboardState {
  dashboardData: DashboardItem[];
  monthlyData: any;
  stateData: any;
  statepaginnation: any;
  topAuditorData: any;
  topVisitorData: any;
  dashboardSummary: DashboardSummaryData | null;
  monthlySuccessMsg: string;
  dashboardSuccessMsg: string;
  dashboardSummarySuccessMsg: string;
  topAuditorDataSuccess: string;
  topVisitorDataSuccess: string;
  dashboardErrorMsg: string;
  dashboardSummaryErrorMsg: string;
  stateDataErrorMsg: string;
  monthlyErrorMsg: string;
  topAuditorErrorMsg: string;
  topVisitorErrorMsg: string;
  loading: boolean;
  dashboardSummaryLoading: boolean;
  apiError: Record<string, any>;
  syncSuccessMsg: string;
  syncErrorMSg: string;
   isDashboardrefresh: boolean
}

const initialState: DashboardState = {
  dashboardData: [],
  monthlyData: [],
  stateData:[],
  statepaginnation:{},
  topAuditorData:[],
  topVisitorData:[],
  dashboardSummary: null,
  dashboardSuccessMsg: "",
  dashboardSummarySuccessMsg: "",
  topAuditorDataSuccess: "",
  monthlySuccessMsg: "",
  topVisitorDataSuccess: "",
  dashboardErrorMsg: "",
  dashboardSummaryErrorMsg: "",
  monthlyErrorMsg: "",
  stateDataErrorMsg: "",
  topAuditorErrorMsg: "",
  topVisitorErrorMsg: "",
  syncSuccessMsg:"",
  syncErrorMSg:"",
  loading: false,
  dashboardSummaryLoading: false,
  apiError: {},
  isDashboardrefresh: false
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    // MonthlyStats
    fetchMonthlyStatsSuccess(state, action) {
      state.loading = false;
      // Store only the "data" object from the API response
      state.dashboardData = action.payload.data || [];
      state.dashboardSuccessMsg = "Dashboard data fetched successfully.";
      state.isDashboardrefresh = false;
    },
    fetchMonthlyStatsError(state, action) {
      state.loading = false;
      state.dashboardErrorMsg = action.payload as string;
    },
    // Overview
    fetchauditorsviewSuccess(state, action) {
      state.loading = false;
      // Store only the "data" object from the API response
      state.monthlyData = action.payload.data;
      state.monthlySuccessMsg = "Auditors Statistics fetched successfully.";
      state.isDashboardrefresh = false;
    },
    fetchauditorsviewError(state, action) {
      state.loading = false;
      state.monthlyErrorMsg = action.payload as string;
    },
    // State Report
    fetchStateReportSuccess(state, action) {
      state.loading = false;
      // Store only the "data" object from the API response
      state.stateData = action.payload.data.result;
      state.statepaginnation = action.payload.data.paginnation;
      state.monthlySuccessMsg = "State Statistics fetched successfully.";
      state.isDashboardrefresh = false;
    },
    fetchStateReportError(state, action) {
      state.loading = false;
      state.stateDataErrorMsg = action.payload as string;
    },

    // Top Auditors
    topAuditorDataSuccess(state, action) {
      state.loading = false;
      // Store only the "data" object from the API response
      state.topAuditorData = action.payload.data;
      state.topAuditorDataSuccess = "Top Auditors Statistics fetched successfully.";
      state.isDashboardrefresh = false;
    },
    topAuditorDataError(state, action) {
      state.loading = false;
      state.topAuditorErrorMsg = action.payload as string;
    },

    // Top Visitors
    topVisitsDataSuccess(state, action) {
      state.loading = false;
      // Store only the "data" object from the API response
      state.topVisitorData = action.payload.data;
      state.topVisitorDataSuccess = "Top Visitors Statistics fetched successfully.";
      state.isDashboardrefresh = false;
    },
    topVisitsDataError(state, action) {
      state.loading = false;
      state.topVisitorErrorMsg = action.payload as string;
    },

    // Dashboard Sync Data
    fetchSyncStatusSuccess(state, action) {
      state.loading = false;
      state.syncSuccessMsg= "Sync data is fetched successfully";
      state.isDashboardrefresh = false;
    },
    fetchSyncStatusError(state, action) {
      state.loading = false;
      state.syncErrorMSg = action.payload as string;
    },

    setLoading(state, action) {
      state.loading = action.payload;
    },

    refreshDashboard(state,action) {
      state.isDashboardrefresh = action.payload
    },

    // Dashboard Summary
    fetchDashboardSummarySuccess(state, action) {
      state.dashboardSummaryLoading = false;
      state.dashboardSummary = action.payload.data;
      state.dashboardSummarySuccessMsg = "Dashboard summary fetched successfully.";
      state.isDashboardrefresh = false;
    },
    fetchDashboardSummaryError(state, action) {
      state.dashboardSummaryLoading = false;
      state.dashboardSummaryErrorMsg = action.payload as string;
    },
    setDashboardSummaryLoading(state, action) {
      state.dashboardSummaryLoading = action.payload;
    },
    resetDashboardState(state) {
      state.dashboardSummary = null;
      state.dashboardSummaryErrorMsg = "";
      state.dashboardSummarySuccessMsg = "";
      state.dashboardSummaryLoading = false;
      state.dashboardErrorMsg = "";
      state.dashboardSuccessMsg = "";
      state.loading = false;
    },
  },
});

export const { 
  fetchMonthlyStatsSuccess, 
  fetchMonthlyStatsError,
  fetchSyncStatusSuccess,
  fetchSyncStatusError, 
  setLoading, 
  fetchauditorsviewSuccess,
  fetchauditorsviewError,
  fetchStateReportSuccess,
  fetchStateReportError,
  topAuditorDataSuccess,
  topAuditorDataError,
  topVisitsDataSuccess,
  topVisitsDataError,
  refreshDashboard,
  fetchDashboardSummarySuccess,
  fetchDashboardSummaryError,
  setDashboardSummaryLoading,
  resetDashboardState
} = dashboardSlice.actions;
export default dashboardSlice.reducer;